/**
 * Voice-routing helpers extracted so both the live Web Speech hook AND the
 * post-recording Whisper flow can split a transcript into discipline-specific
 * slots based on spoken cue words.
 *
 * A "routing" object looks like:
 *   {
 *     slots: [{ key: "eval_1", label: "Assessment" }, ...],
 *     defaultSlotKey: "eval_1"
 *   }
 *
 * Cue words: spoken bare label OR cue-word + label (section <label>, note <label>, ...)
 */

export const CUE_WORDS = ["section", "note", "topic", "field"];

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function buildCueRegex(routing) {
  if (!routing || !routing.slots || !routing.slots.length) return null;
  const labels = routing.slots
    .map((s) => s.label.toLowerCase())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  if (!labels.length) return null;
  const labelGroup = labels.map(escapeRegex).join("|");
  const cueGroup = CUE_WORDS.join("|");
  return new RegExp(
    `\\b(?:(?:${cueGroup})\\s+)?(${labelGroup})\\b[\\s:,.\\-]*`,
    "gi"
  );
}

function findSlotByLabel(routing, label) {
  if (!routing || !label) return null;
  const norm = label.toLowerCase().trim();
  return routing.slots.find((s) => s.label.toLowerCase() === norm) || null;
}

/**
 * Route a single (live) transcript chunk into the slot map by cue-word splits.
 */
export function routeChunk(chunk, routing, activeSlotKey, slots) {
  const updated = { ...slots };
  let activeKey = activeSlotKey || (routing.defaultSlotKey || routing.slots[0]?.key);
  const regex = buildCueRegex(routing);

  if (!regex) {
    updated[activeKey] = ((updated[activeKey] || "") + " " + chunk).trim();
    return { slots: updated, activeKey };
  }

  let lastIndex = 0;
  const matches = [];
  let m;
  while ((m = regex.exec(chunk)) !== null) {
    matches.push({ index: m.index, length: m[0].length, label: m[1] });
  }

  if (!matches.length) {
    updated[activeKey] = ((updated[activeKey] || "") + " " + chunk).trim();
    return { slots: updated, activeKey };
  }

  matches.forEach((match) => {
    const pre = chunk.slice(lastIndex, match.index).trim();
    if (pre) {
      updated[activeKey] = ((updated[activeKey] || "") + " " + pre).trim();
    }
    const slot = findSlotByLabel(routing, match.label);
    if (slot) activeKey = slot.key;
    lastIndex = match.index + match.length;
  });

  const tail = chunk.slice(lastIndex).trim();
  if (tail) updated[activeKey] = ((updated[activeKey] || "") + " " + tail).trim();
  return { slots: updated, activeKey };
}

/**
 * One-shot: route a whole transcript (Whisper output) into a fresh slot map.
 * Returns { [slotKey]: text }.
 */
export function routeTranscriptToSlots(transcript, routing) {
  if (!transcript || !routing) return {};
  const empty = routing.slots.reduce((acc, s) => {
    acc[s.key] = "";
    return acc;
  }, {});
  const { slots } = routeChunk(transcript, routing, routing.defaultSlotKey || routing.slots[0]?.key, empty);
  return slots;
}
